import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../Icon';
import { games } from '../../games';
import SharedControls from '../../SharedControls';

export default function GamesMenu() {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const root = useRef(null);
  const trigger = useRef(null);
  const items = useRef([]);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (open) items.current[activeIndex]?.focus();
  }, [open, activeIndex]);
  useEffect(() => {
    if (!open) return;
    const dismiss = event => {
      if (root.current?.closest('.recess-controls.is-open')?.contains(event.target)) return;
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  function show(index = 0) {
    setActiveIndex(index);
    setOpen(true);
  }
  function close(restoreFocus = false) {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  }
  function handleKeys(event) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (open && event.key === ' ' && event.target.getAttribute('role') === 'menuitem') {
      event.preventDefault();
      items.current[activeIndex]?.click();
      return;
    }
    const forward = event.key === 'ArrowDown' || (open && event.key === 'ArrowRight');
    const backward = event.key === 'ArrowUp' || (open && event.key === 'ArrowLeft');
    if (forward || backward) {
      event.preventDefault();
      if (!open) show(forward ? 0 : games.length - 1);
      else setActiveIndex(index => (index + (forward ? 1 : -1) + games.length) % games.length);
    } else if (open && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : games.length - 1);
    } else if (open && !event.ctrlKey && !event.metaKey && !event.altKey && event.key.length === 1 && /[a-z]/i.test(event.key)) {
      const next = games.map((_, offset) => (activeIndex + offset + 1) % games.length)
        .find(index => games[index].name.toLowerCase().startsWith(event.key.toLowerCase()));
      if (next !== undefined) {
        event.preventDefault();
        setActiveIndex(next);
      }
    }
  }

  return <SharedControls><div className="games-switcher" ref={root} onKeyDown={handleKeys} onBlur={event => {
    if (event.currentTarget.closest('.recess-controls.is-open')?.contains(event.relatedTarget)) return;
    if (!event.currentTarget.contains(event.relatedTarget)) close();
  }}>
    <button ref={trigger} id="games-trigger" className="games-toggle" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? 'games-menu' : undefined} onClick={() => open ? close(true) : show()}>
      Games <Icon name="chevron-down" />
    </button>
    {open && <div className="games-popover">
      <p className="games-menu-heading">A game for every kind of night.</p>
      <div id="games-menu" role="menu" aria-labelledby="games-trigger" className="games-menu">
        {games.map((game, index) => {
          const current = game.id === 'drink-if-you-think';
          return <a key={game.id} ref={element => { items.current[index] = element; }} role="menuitem" tabIndex={activeIndex === index ? 0 : -1}
            href={current ? '/' : game.url} aria-current={current ? 'true' : undefined}
            className={`games-menu-item${current ? ' current-game' : ''}`}
            onFocus={() => setActiveIndex(index)} onClick={event => {
              if (current) {
                event.preventDefault();
                close(true);
              } else close();
            }}>
            <span className="games-menu-copy"><span className="games-menu-title">{game.name}</span><span className="games-menu-description">{game.description}</span>{current && <span className="current-game-label">Current game</span>}</span>
            <Icon name={current ? 'check' : 'arrow-right'} />
          </a>;
        })}
      </div>
    </div>}
  </div></SharedControls>;
}
